import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { FileUploader } from "react-drag-drop-files";
import { FaCheckCircle, FaEdit, FaTimes, FaUpload } from "react-icons/fa";
import { GiMoneyStack } from "react-icons/gi";
import SelectSearch from "react-select-search";
import { useTranslation } from "react-i18next";

import { INPUT_TYPE } from "../constants";
import { isValidEmail, isValidNumber } from "../utils/helperFunctions";
import i18n from "../i18n";

// Style constants
const themeColors = {
  primary: "#6EC3C4",
  secondary: "#FF7F7F",
  background: "#f8f9fa",
};

const inputStyles = {
  border: `2px solid ${themeColors.primary}`,
  borderRadius: "8px",
  "&:focus": {
    borderColor: themeColors.secondary,
    boxShadow: `0 0 0 0.2rem ${themeColors.primary}40`,
  },
};

const fileUploaderStyle = {
  border: `2px dashed ${themeColors.primary}`,
  borderRadius: "12px",
  padding: "1.5rem",
  backgroundColor: "#fff",
  cursor: "pointer",
  transition: "all 0.3s ease",
  ":hover": {
    backgroundColor: `${themeColors.primary}15`,
    borderColor: themeColors.secondary,
  },
};

export const EditableModal = (props) => {
  const { actionLabel, actionOnClick, title, configs = [], currentValue = {} } = props;
  const { t } = useTranslation(); // Accessing translation function
  const [showModal, setShowModal] = useState(false);
  const [data, setData] = useState(currentValue);
  const [errMsg, setErrMsg] = useState("");
  useEffect(() => {
    setData(
      configs.reduce(
        (accumulate, item) => ({
          ...accumulate,
          [item.fieldName]: currentValue[item.fieldName] || "",
        }),
        {}
      )
    );
  }, [showModal]);

  const onChange = (e) => {
    setData((prevState) => ({
      ...prevState,
      [e.target.id]: e.target.value,
    }));
  };

  const modalBody = (
    <div style={{ padding: "1rem" }}>
      <Form>
        {configs.map((item) => {
          const { fieldName, label, inputType, hideWhenEdit, required } = item;
          if (hideWhenEdit) {
            return null;
          }
          const displayLabel = required ? `${label}*` : label;
          const labelKey = `EditableModal.fields.${label}`;
          const translatedLabel = i18n.exists(labelKey) ? t(labelKey) : label; ///t(); // Using the translation for the label

          switch (inputType) {
            case INPUT_TYPE.TEXT:
            case INPUT_TYPE.EMAIL:
              return (
                <div key={fieldName} style={{ margin: "10px 0" }}>
                  <Form.Label>{translatedLabel}</Form.Label>
                  <div style={{ display: "flex" }}>
                    <Form.Control type="text" id={fieldName} value={data[fieldName]} onChange={onChange} />
                  </div>
                </div>
              );
            case INPUT_TYPE.NUMBER:
              return (
                <div key={fieldName} style={{ margin: "10px 0" }}>
                  <Form.Label>{translatedLabel}</Form.Label>
                  <div style={{ display: "flex" }}>
                    <Form.Control type="number" id={fieldName} value={data[fieldName]} onChange={onChange} />
                  </div>
                </div>
              );
            case INPUT_TYPE.TEXT_AREA:
              return (
                <div key={fieldName} style={{ margin: "10px 0" }}>
                  <Form.Label>{translatedLabel}</Form.Label>
                  <div style={{ display: "flex" }}>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      id={fieldName}
                      maxLength={500}
                      value={data[fieldName]}
                      onChange={onChange}
                    />
                  </div>
                </div>
              );
            case INPUT_TYPE.DATE:
              return (
                <div key={fieldName} style={{ margin: "10px 0" }}>
                  <Form.Label>{translatedLabel}</Form.Label>
                  <div style={{ display: "flex" }}>
                    <Form.Control type="date" id={fieldName} value={data[fieldName]} onChange={onChange} />
                  </div>
                </div>
              );
            case INPUT_TYPE.BOOLEAN:
              return (
                <div key={fieldName} style={{ margin: "10px 0" }}>
                  <div style={{ display: "flex" }}>
                    <Form.Check
                      type="switch"
                      label={translatedLabel}
                      id={fieldName}
                      checked={data[fieldName]}
                      onChange={(e) => {
                        setData((prevState) => ({
                          ...prevState,
                          [fieldName]: !data[fieldName],
                        }));
                      }}
                    />
                  </div>
                </div>
              );
            case INPUT_TYPE.SELECT:
              return (
                <div key={fieldName} style={{ margin: "10px 0" }}>
                  <Form.Label>{translatedLabel}</Form.Label>
                  <div style={{ display: "flex" }}>
                    <SelectSearch
                      options={item.enumValues.map((enumItem) => {
                        const { name, value } = enumItem;
                        const enumKey = `EditableModal.selectValues.${name}`;
                        const enumKeyExist = i18n.exists(enumKey);
                        return { name: enumKeyExist ? t(enumKey) : name, value };
                      })}
                      onChange={(val) => {
                        setData((prevState) => ({
                          ...prevState,
                          [fieldName]: val,
                        }));
                      }}
                      value={data[fieldName]}
                      name={fieldName}
                      placeholder={translatedLabel}
                      search={true}
                    />
                  </div>
                </div>
              );
            case INPUT_TYPE.FILE:
              return (
                <div key={fieldName} style={{ margin: "1rem 0" }}>
                  <Form.Label className="fw-bold text-secondary">{translatedLabel}</Form.Label>
                  <FileUploader
                    style={{ width: "100%" }}
                    handleChange={(file) => setData({ ...data, [fieldName]: file })}
                    multiple={false}
                    hoverTitle={
                      <>
                        <FaUpload className="me-2" />
                        {t("EditableModal.header.upload")}
                      </>
                    }
                    types={item.fileTypes}
                  >
                    <div style={fileUploaderStyle} className="text-center">
                      <FaUpload className="text-primary mb-2" size="1.5em" />
                      <div className="text-muted">{t("EditableModal.header.upload")}</div>
                      <small className="text-muted">
                        {t("EditableModal.header.supportedFormats")}: {item.fileTypes.join(", ")}
                      </small>
                    </div>
                  </FileUploader>
                </div>
              );
            default:
              return (
                <div className="form-group" key={fieldName} style={{ margin: "10px 0" }}>
                  {t("EditableModal.header.notSupportedInput")}
                </div>
              );
          }
        })}
      </Form>
    </div>
  );

  return (
    <>
      <Button variant="outline-success" onClick={() => setShowModal(true)} className="d-flex align-items-center gap-2">
        <FaEdit />
        {t(title)}
      </Button>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header
          closeButton
          style={{
            backgroundColor: themeColors.primary,
            color: "white",
          }}
        >
          <Modal.Title className="d-flex align-items-center gap-2">
            <GiMoneyStack size="1.2em" />
            {title}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ backgroundColor: themeColors.background }}>
          {modalBody}

          {errMsg && (
            <Alert variant="danger" className="rounded-pill d-flex align-items-center gap-2">
              <FaTimes />
              {errMsg.split("---").map((msg, i) => (
                <div key={i}>{msg}</div>
              ))}
            </Alert>
          )}
        </Modal.Body>

        <Modal.Footer style={{ backgroundColor: themeColors.background }}>
          <Button
            variant="outline-secondary"
            onClick={() => setShowModal(false)}
            className="d-flex align-items-center gap-2"
          >
            <FaTimes />
            {t("EditableModal.header.cancel")}
          </Button>
          <Button
            variant="success"
            onClick={() => {
              var msg = "";
              var updatedValues = {};
              for (var i = 0; i < configs.length; i++) {
                const config = configs[i];
                const { required, fieldName, label, inputType, hideWhenEdit } = config;
                if (hideWhenEdit) {
                  continue;
                }
                const fieldValue = data[fieldName];
                const currentFieldValue = currentValue[fieldName];
                if (required && (!fieldValue || fieldValue.length === 0)) {
                  msg += `---${t("EditableModal.header.requiredField", { field: label })}`;
                }
                if (inputType === INPUT_TYPE.NUMBER && !isValidNumber(fieldValue)) {
                  msg += `---${t("EditableModal.header.invalidNumber", { field: label })}`;
                }
                if (inputType === INPUT_TYPE.EMAIL && !isValidEmail(fieldValue)) {
                  msg += `---${t("EditableModal.header.invalidEmail", { field: label })}`;
                }
                updatedValues[fieldName] = fieldValue;
              }
              if (msg.length) {
                setErrMsg(msg.substring(3));
                return;
              }
              actionOnClick(updatedValues);
              setShowModal(false);
            }}
            className="d-flex align-items-center gap-2"
          >
            <FaCheckCircle />
            {actionLabel ? actionLabel : t("EditableModal.header.save")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
